import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  cloneAnnouncement,
  createAnnouncement,
  deleteAnnouncement,
  getAnnouncement,
  getAnnouncementEvent,
  setAnnouncementEventFailed,
  setAnnouncementEventSuccess,
  getAnnouncementFailed,
  getAnnouncementSuccess,
  getAnnouncementTemplates,
  getSentAnnouncements,
  setCloneAnnouncement,
  setCloneAnnouncementFailed,
  setCreateAnnouncement,
  setCreateAnnouncementFailed,
  setDeleteAnnouncement,
  setDeleteAnnouncementFailed,
  setPublishAnnouncement,
  setPublishAnnouncementFailed,
  setPublishAnnouncementSuccess,
  setAnnouncementTemplatesFetchError,
  setAnnouncementTemplates,
  setSentAnnouncementFetchError,
  setSentAnnouncements,
  setUpdateAnnouncement,
  setUpdateAnnouncementFailed,
  updateAnnouncement,
} from './announcementSlice';
import { IpaUserExtended, selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import { AxiosResponse } from 'axios';
import {
  AnnouncementMethods,
  AnnouncementTemplateDto,
  AnnouncementsApi,
  Configuration,
  SearchAnnouncementTemplatesParamsDto,
  SearchSentAnnouncementsDto,
  UpdateAnnouncementDto,
} from '@inosio/payments-sdk';

const { REACT_APP_IPA_BASE_PATH } = process.env;

const apiConfig = new Configuration({
  basePath: REACT_APP_IPA_BASE_PATH,
});

export function requestAnnouncementEvent(accessToken: string, eventUuid: string) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.getAnnouncementEvent(eventUuid);
}

export function requestAnnouncement(
  accessToken: string,
  payload: {
    uuid: string;
  },
) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.getAnnouncementTemplate(payload.uuid);
}

export function requestSearchAnnouncementTemplates(accessToken: string, announcementSearchParamsDto: SearchAnnouncementTemplatesParamsDto) {
  apiConfig.accessToken = accessToken;
  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.searchForAnnouncementTemplates(announcementSearchParamsDto);
}

export function requestSearchSentAnnouncements(accessToken: string, announcementSearchParamsDto: SearchSentAnnouncementsDto) {
  apiConfig.accessToken = accessToken;
  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.searchForSentAnnouncements(announcementSearchParamsDto);
}

export function requestCreateAnnouncement(accessToken: string, createAnnouncementDto: AnnouncementTemplateDto) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.createAnnouncementTemplate(createAnnouncementDto);
}

export function requestCloneAnnouncement(accessToken: string, cloneFromAnnouncementTemplateUuid: string, announcementMethod: AnnouncementMethods) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.createAnnouncementTemplate(
    {
      AnnouncementMethod: announcementMethod,
    } as AnnouncementTemplateDto,
    cloneFromAnnouncementTemplateUuid,
  );
}

export function requestUpdateAnnouncement(accessToken: string, payload: { uuid: string; updateAnnouncementDto: UpdateAnnouncementDto }) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.updateAnnouncementTemplate(payload.uuid, payload.updateAnnouncementDto);
}

export function requestDeleteAnnouncement(accessToken: string, payload: { uuid: string }) {
  apiConfig.accessToken = accessToken;

  const ipaAnnouncementApi = new AnnouncementsApi(apiConfig);
  return ipaAnnouncementApi.deleteAnnouncementTemplate(payload.uuid);
}

export function* handleFetchAnnouncement(action: { type: typeof getAnnouncement; payload: { uuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestAnnouncement, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(getAnnouncementSuccess(data));
  } catch (error: any) {
    yield put(
      getAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleFetchAnnouncementEvent(action: { type: typeof getAnnouncement; payload: { eventUuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestAnnouncementEvent, currentUser.accessToken, action.payload.eventUuid);
    const { data } = response;
    yield put(setAnnouncementEventSuccess(data));
  } catch (error: any) {
    yield put(
      setAnnouncementEventFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleSearchAnnouncementTemplates(action: { type: typeof getAnnouncementTemplates; payload: SearchAnnouncementTemplatesParamsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestSearchAnnouncementTemplates, currentUser.accessToken, action.payload);
    const { data } = response;

    yield put(setAnnouncementTemplates(data));
  } catch (error: any) {
    yield put(
      setAnnouncementTemplatesFetchError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleSearchSentAnnouncements(action: { type: typeof getSentAnnouncements; payload: SearchSentAnnouncementsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestSearchSentAnnouncements, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setSentAnnouncements(data));
  } catch (error: any) {
    yield put(
      setSentAnnouncementFetchError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleCreateAnnouncement(action: { type: typeof createAnnouncement; payload: AnnouncementTemplateDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestCreateAnnouncement, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setCreateAnnouncement(data));
  } catch (error: any) {
    yield put(
      setCreateAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleCloneAnnouncement(action: {
  type: typeof createAnnouncement;
  payload: {
    templateUuid: string;
    announcementMethod: AnnouncementMethods;
  };
}) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestCloneAnnouncement, currentUser.accessToken, action.payload.templateUuid, action.payload.announcementMethod);
    const { data } = response;
    yield put(setCloneAnnouncement(data));
  } catch (error: any) {
    yield put(
      setCloneAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleUpdateAnnouncement(action: { type: typeof updateAnnouncement; payload: { uuid: string; updateAnnouncementDto: UpdateAnnouncementDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestUpdateAnnouncement, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setUpdateAnnouncement(data));
  } catch (error: any) {
    yield put(
      setUpdateAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleDeleteAnnouncement(action: { type: typeof deleteAnnouncement; payload: { uuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestDeleteAnnouncement, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setDeleteAnnouncement(data));
  } catch (error: any) {
    yield put(
      setDeleteAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handlePublishAnnouncement(action: { type: typeof updateAnnouncement; payload: { uuid: string; updateAnnouncementDto: UpdateAnnouncementDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestUpdateAnnouncement, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setPublishAnnouncementSuccess(data));
  } catch (error: any) {
    yield put(
      setPublishAnnouncementFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* announcementSaga() {
  yield takeLatest(getAnnouncement.type, handleFetchAnnouncement);
  yield takeLatest(getAnnouncementEvent.type, handleFetchAnnouncementEvent);
  yield takeLatest(getAnnouncementTemplates.type, handleSearchAnnouncementTemplates);
  yield takeLatest(getSentAnnouncements.type, handleSearchSentAnnouncements);
  yield takeLatest(createAnnouncement.type, handleCreateAnnouncement);
  yield takeLatest(cloneAnnouncement.type, handleCloneAnnouncement);
  yield takeLatest(updateAnnouncement.type, handleUpdateAnnouncement);
  yield takeLatest(deleteAnnouncement.type, handleDeleteAnnouncement);
  yield takeLatest(setPublishAnnouncement.type, handlePublishAnnouncement);
}
